import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IResponse, IStandardResponse } from "../../orders/models/order";
import { EWarehouseEndPoint, warehouseApi } from "../services/warehouseApi";
import { IWarehouseInvoice, UseFetchOptions } from "../models/warehouse";
import {
  IApprovedDn,
  IApprovedDnGrouped,
  IAuditLogDN,
  IInputStock,
  IStockDataResponse,
  IStockDetails,
  Tag,
} from "../models/stock";
import {
  IAccWarehouseInvoice,
  ICategoryAccessory,
} from "../models/accWarehouse";

interface IDnGroupedVariables {
  category: string;
  fetchType: string;
  queryOptions?: {
    onSuccess?: (data: IStockDataResponse) => void;
    enabled?: boolean;
  };
}

interface IHistoryVariables {
  id: any;
  type: string;
  queryOptions?: {
    onSuccess?: (data: IStockDataResponse) => void;
    enabled?: boolean;
  };
}

export const useGetAllYarnInvoice = () => {
  const query = useQuery<IStandardResponse<Array<IWarehouseInvoice>>, Error>({
    queryKey: [EWarehouseEndPoint.ALL_YARN_INVOICE],
    queryFn: () => warehouseApi.getAllYarnInvoice(),
    enabled: false,
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
  };
};

export const useGetAllAccInvoice = () => {
  const query = useQuery<IStandardResponse<Array<IWarehouseInvoice>>, Error>({
    queryKey: [EWarehouseEndPoint.ALL_YARN_INVOICE],
    queryFn: () => warehouseApi.getAllAccInvoice(),
    enabled: false,
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
  };
};

export const useGetAllExistingYarnAccLycra = (
  type: "yarn" | "accessory" | "lycra"
) => {
  const query = useQuery<IStandardResponse<Array<IStockDetails>>, Error>({
    queryKey: [EWarehouseEndPoint.ALL_YARN || EWarehouseEndPoint.ALL_ACCESSORY],
    queryFn: () => warehouseApi.getAllExistingYarnAccLycra(type),
  });
  const { data } = query.data || {};
  return {
    ...query,
    data: data,
  };
};

export const useGetPackingByYarnInvoice = (num_invoice: string) => {
  const query = useQuery<IStandardResponse<IWarehouseInvoice>, Error>({
    queryKey: [EWarehouseEndPoint.ALL_YARN_INVOICE, num_invoice],
    queryFn: () => warehouseApi.getPackingByYarnInvoice(num_invoice),
    enabled: !!num_invoice || num_invoice !== "",
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
  };
};

export const useGetPackingByAccInvoice = (num_invoice: string) => {
  const query = useQuery<IStandardResponse<IAccWarehouseInvoice>, Error>({
    queryKey: [EWarehouseEndPoint.ALL_YARN_INVOICE, num_invoice],
    queryFn: () => warehouseApi.getPackingByAccInvoice(num_invoice),
    enabled: num_invoice !== "",
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
  };
};

export const useGetStockHistoryById = <T,>({
  id,
  type,
  queryOptions = {},
}: IHistoryVariables) => {
  const query = useQuery<IStandardResponse<Array<T>>, Error>({
    queryKey: [EWarehouseEndPoint.STOCK_HISTORY, id, type],
    queryFn: () => warehouseApi.getHistoryById(id, type),
    enabled: queryOptions.enabled ?? false,
  });

  const { data } = query.data || {};
  return { ...query, data: data };
};

export const useGetAllStock = ({
    page = 1,
    limit,
    search = {},
    sort = "",
    global_search = "",
    queryOptions = {},
    category,
  }: UseFetchOptions<IStockDataResponse>
) => {
  const queryClient = useQueryClient();
  // Serialize search to ensure stable key
  const searchKey = JSON.stringify(search);

  const queryKey = [
    EWarehouseEndPoint.ALL_STOCK,
    category,
    page,
    limit,
    searchKey,
    sort,
    global_search,
  ];

  const previousData =
    queryClient.getQueryData<IResponse<IStockDataResponse>>(queryKey);

  const query = useQuery<IResponse<IStockDataResponse>, Error>({
    queryKey,
    queryFn: () =>
      warehouseApi.getAllStock(
        page,
        limit,
        search,
        sort,
        global_search,
        category
      ),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
    enabled: !!category,
  });

  const { data } = query.data || {};

  // Function to refetch all useGetAllStock queries
  const refetchAllStock = () => {
    queryClient.invalidateQueries({
      queryKey: [EWarehouseEndPoint.ALL_STOCK],
      refetchType: "active", // Refetch only active queries (can be "all" to include inactive)
    });
  };

  return {
    ...query,
    data: data,
    pagination: {
      limit: data?.limit,
      page: data?.page,
      total: data?.total,
      totalPages: data?.totalPages,
    },
    refetchAllStock,
  };
};

export const useGetApprovedDN = ({
  page = 1,
  limit,
  search = {},
  sort = "",
  global_search = "",
  queryOptions = {},
  category,
  fetchType,
}: UseFetchOptions<IApprovedDn>) => {
  const queryClient = useQueryClient();
  const searchKey = JSON.stringify(search);

  const queryKey = [
    EWarehouseEndPoint.KEY_APPROVED,
    category,
    fetchType,
    page,
    limit,
    searchKey,
    sort,
    global_search,
  ];

  const previousData =
    queryClient.getQueryData<IResponse<Array<IApprovedDn>>>(queryKey);

  const query = useQuery<IResponse<Array<IApprovedDn>>, Error>({
    queryKey,
    queryFn: () =>
      warehouseApi.getAllApprovedDN(
        page,
        limit,
        search,
        sort,
        global_search,
        category,
        fetchType
      ),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    enabled: queryOptions.enabled ?? false,
    ...queryOptions,
  });

  const { data } = query.data || {};

  const refetchApproved = () => {
    queryClient.invalidateQueries({
      queryKey: [EWarehouseEndPoint.KEY_APPROVED],
      refetchType: "active",
    });
  };

  return {
    ...query,
    data: data,
    pagination: {
      limit: data?.limit,
      page: data?.page,
      total: data?.total,
      totalPages: data?.totalPages,
    },
    refetchApproved,
  };
};

export const useGetAllStockAvailable = ({
  page = 1,
  limit,
  search = {},
  sort = "",
  global_search = "",
  queryOptions = {},
  category,
}: UseFetchOptions<IStockDataResponse>) => {
  const queryClient = useQueryClient();

  // Serialize search to ensure stable key
  const searchKey = JSON.stringify(search);

  const queryKey = [
    EWarehouseEndPoint.ALL_STOCK_AVAILABLE,
    category,
    page,
    limit,
    searchKey,
    sort,
    global_search,
  ];

  const previousData =
    queryClient.getQueryData<IResponse<IStockDataResponse>>(queryKey);

  const query = useQuery<IResponse<IStockDataResponse>, Error>({
    queryKey: queryKey,
    queryFn: () =>
      warehouseApi.getAllStockAvailable(
        page,
        limit,
        search,
        sort,
        global_search,
        category
      ),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    enabled: queryOptions.enabled ?? false,
    ...queryOptions,
  });

  const { data } = query.data || {};

  // Function to refetch all useGetAllStock queries
  const refetchAllStockAvailable = () => {
    queryClient.invalidateQueries({
      queryKey: [EWarehouseEndPoint.ALL_STOCK_AVAILABLE],
      refetchType: "active", // Refetch only active queries (can be "all" to include inactive)
    });
  };

  return {
    ...query,
    data: data,
    pagination: {
      limit: data?.limit,
      page: data?.page,
      total: data?.total,
      totalPages: data?.totalPages,
    },
    refetchAllStockAvailable,
  };
};

export const useGetAllStockAvailablebyIds = ({
  page = 1,
  limit,
  search = {},
  warehouseIds,
  sort = "",
  global_search = "",
  queryOptions = {},
  category,
}: UseFetchOptions<IStockDataResponse> & { warehouseIds?: string[] }) => {

  const queryKey = [
    EWarehouseEndPoint.ALL_STOCK_AVAILABLE,
    category,
    page,
    limit,
    JSON.stringify(search),
    warehouseIds?.join(",") || "",
    sort,
    global_search,
  ];

  const query = useQuery<IResponse<IStockDataResponse>, Error>({
    queryKey,
    queryFn: () =>
      warehouseApi.getAllStockAvailableByIds(
        page,
        limit,
        warehouseIds ? { ...search, reference: warehouseIds } : search,
        sort,
        global_search,
        category
      ),
    ...queryOptions,
  });

  return {
    ...query,
    data: query.data?.data,
    pagination: {
      limit: query.data?.data?.limit,
      page: query.data?.data?.page,
      total: query.data?.data?.total,
      totalPages: query.data?.data?.totalPages,
    },
  };
};

interface IYarnStockSelectionFilters {
  colorCodes: string[];
  yarnCompo?: string;
  yarnCount?: string;
  enabled?: boolean;
}

export const useGetYarnStockForSelection = ({
  colorCodes,
  yarnCompo,
  yarnCount,
  enabled = false,
}: IYarnStockSelectionFilters) => {
  return useQuery<IStockDataResponse[], Error>({
    queryKey: [
      EWarehouseEndPoint.ALL_STOCK_AVAILABLE_BY_IDS,
      "yarn-selection",
      colorCodes,
      yarnCompo,
      yarnCount,
    ],
    queryFn: async () => {
      const colorSearches: Record<string, string>[] = [
        ...colorCodes.map((colorCode) => ({
          "yarn.color_code": `contains,${colorCode}`,
        })),
        { "yarn.color_name": "contains,RAW WHITE" },
      ];
      const responses = await Promise.all(
        colorSearches.map((colorSearch) =>
          warehouseApi.getYarnStockForSelection({
            ...colorSearch,
            ...(yarnCompo
              ? { "yarn.yarn_compo": `contains,${yarnCompo}` }
              : {}),
            ...(yarnCount
              ? { "yarn.yarn_count": `contains,${yarnCount}` }
              : {}),
            tag: [],
            onlyAvailable: 1,
          })
        )
      );

      const stocks = responses.flatMap((response) =>
        response.data?.data ?? []
      );

      return Array.from(
        new Map(stocks.map((stock) => [stock.id, stock])).values()
      );
    },
    enabled,
  });
};

interface IAccessoryStockSelectionFilters {
  colorCodes: string[];
  reference?: string;
  name?: string;
  size?: string;
  enabled?: boolean;
}

export const useGetAccessoryStockForSelection = ({
  colorCodes,
  reference,
  name,
  size,
  enabled = false,
}: IAccessoryStockSelectionFilters) => {
  return useQuery<IStockDataResponse[], Error>({
    queryKey: [
      EWarehouseEndPoint.ALL_STOCK_AVAILABLE_BY_IDS,
      "accessory-selection",
      colorCodes,
      reference,
      name,
      size,
    ],
    queryFn: async () => {
      const colorSearches = colorCodes.length > 0 ? colorCodes : [undefined];
      const responses = await Promise.all(
        colorSearches.map((colorCode) =>
          warehouseApi.getAccessoryStockForSelection({
            ...(colorCode
              ? { "accessory.color_code": `contains,${colorCode}` }
              : {}),
            ...(reference
              ? { "accessory.reference": `contains,${reference}` }
              : {}),
            ...(name ? { "accessory.name": `contains,${name}` } : {}),
            ...(size ? { "accessory.size": `contains,${size}` } : {}),
            tag: [],
            onlyAvailable: 1,
          })
        )
      );

      const stocks = responses.flatMap((response) =>
        response.data?.data ?? []
      );

      return Array.from(
        new Map(stocks.map((stock) => [stock.id, stock])).values()
      );
    },
    enabled,
  });
};

export const useGetGroupedApprovedDn = ({
  category,
  fetchType,
  queryOptions = {},
}: IDnGroupedVariables) => {
  const queryClient = useQueryClient();
  const query = useQuery<IStandardResponse<Array<IApprovedDnGrouped>>, Error>({
    queryKey: [EWarehouseEndPoint.APPROVED_DN_STOCK_ISSUE, category, fetchType],
    queryFn: () => warehouseApi.getApprovedDnGrouped(category, fetchType || ""),
    enabled: queryOptions.enabled ?? false,
    ...queryOptions,
  });
  const { data } = query.data || {};

  // Function to refetch all useGetAllStock queries
  const refetchApprovedStockIssue = (category: string) => {
    queryClient.invalidateQueries({
      queryKey: [EWarehouseEndPoint.APPROVED_DN_STOCK_ISSUE, category],
      refetchType: "active",
    });
  };

  return {
    ...query,
    data: data,
    refetchApprovedStockIssue,
  };
};

export const useUpdateStockDetails = <T,>() => {
  return useMutation<
    IStandardResponse<T>,
    Error,
    { id: number; type: string; input: Omit<IInputStock, "location" | "id"> }
  >({
    mutationFn: ({ id, type, input }) =>
      warehouseApi.updateStockDetails(id, type, input),
  });
};

export const useCreateManualRequest = <T,>() => {
  return useMutation<
    IStandardResponse<T>,
    Error,
    { type: string; input: Omit<IInputStock, "id" | "stock_id"> }
  >({
    mutationFn: ({ type, input }) =>
      warehouseApi.createManualRequest(type, input),
  });
};

export const useGetAuditLogWarehouse = (type: string, id: number) => {
  const query = useQuery<IStandardResponse<Array<IAuditLogDN>>, Error>({
    queryKey: [EWarehouseEndPoint.AUDIT_LOG, id],
    queryFn: () => warehouseApi.getAuditLogs(type, id),
    enabled: !!id,
  });

  const { data } = query.data || {};
  return { ...query, data: data };
};

export const useGetCategoryProvider = () => {
  const query = useQuery<IStandardResponse<Array<ICategoryAccessory>>, Error>({
    queryKey: [EWarehouseEndPoint.CATEGORY_PROVIDER],
    queryFn: () => warehouseApi.getCategoryProvider(),
  });

  const { data } = query.data || {};
  return { ...query, data: data };
};

export function useLogFile(orderCode: string) {
  // orderCode=orderCode.replace(/\//g, "_");
  return useQuery({
    queryKey: ["log", orderCode],
    queryFn: () => warehouseApi.orderfetchLogFile(orderCode),
    enabled: !!orderCode, // only run if orderCode exists
    refetchOnWindowFocus: false, // optional
  });
}

export const useUploadStock = () => {
  return useMutation<IStandardResponse<Omit<IInputStock, "id" | "stock_id">>, Error, FormData>({
    mutationFn: (stock) => warehouseApi.uploadStock(stock),
  });
};

export const useGetAllTag = () => {
  const query = useQuery<IStandardResponse<Array<Tag>>, Error>({
    queryKey: [EWarehouseEndPoint.TAG_ALL],
    queryFn: () => warehouseApi.getAllTag(),
  });

  const { data } = query.data || {};
  return { ...query, data: data };
};

export const useUpdateTag = () => {
  return useMutation<
    IStandardResponse<Tag>,
    Error,
    { id: number; input: Partial<Tag> }
  >({
    mutationFn: ({ id, input }) => warehouseApi.updateTag(id, input),
  });
};

export const useDeleteTag = () => {
  return useMutation<IStandardResponse<Tag>, Error, number>({
    mutationFn: (id) => warehouseApi.deleteTag(id),
  });
};

export const useCreateTag = () => {
  return useMutation<
    IStandardResponse<Tag>,
    Error,
    Tag
  >({
    mutationFn: (tag) =>
      warehouseApi.createTag(tag),
  });
};

export const useSearchReferences = (query: string) => {
  return useQuery({
    queryKey: [EWarehouseEndPoint.REFERENCE, query],
    queryFn: () => warehouseApi.searchReferences(query),
    enabled: !!query && query.length >= 1,  
    staleTime: 1000 * 60 * 5, 
  });
};

export const useCreateAdjustment = () =>
  useMutation<
    any,
    Error,
    {
      category: string;
      stock_id: number;
      new_quantity: number;
      adjustment_type: string;
      reason_comment?: string;
      damage_location?: string;
    }
  >({
    mutationFn: (input) => warehouseApi.createAdjustment(input),
  });

export const useGetAdjustments = (params: {
  page: number;
  limit: number;
  search: Record<string, any>;
  sort: string;
  global_search?: string;
}) =>
  useQuery({
    queryKey: [EWarehouseEndPoint.ADJUSTMENT, JSON.stringify(params)],
    queryFn: () =>
      warehouseApi.getAdjustments(
        params.page, params.limit, params.search, params.sort, params.global_search
      ),
  });

export const useReviewAdjustment = () => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { id: number; action: "approve" | "reject" | "cancel"; comment?: string }>({
    mutationFn: ({ id, action, comment }) => warehouseApi.reviewAdjustment(id, action, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EWarehouseEndPoint.ADJUSTMENT] });
      queryClient.invalidateQueries({ queryKey: [EWarehouseEndPoint.ALL_STOCK_AVAILABLE] });
    },
  });
};
